import { RefObject, useCallback, useEffect, useRef, useState } from 'react'
import { useReducedMotion } from './useReducedMotion'

// Keep in sync with .v2-phone in landing-v2.scss (236px wide, screen at the 1206x2622 screenshot ratio).
const PHONE_WIDTH = 236
const PHONE_HEIGHT = 495

interface UseHowItWorksScrollResult {
  sectionRef: RefObject<HTMLElement>
  stageRef: RefObject<HTMLDivElement>
  phoneColumnRef: RefObject<HTMLDivElement>
  activeStep: number
  scrollToStep: (step: number) => void
}

/**
 * Drives the "How it works" sticky-scroll section: the outer section is tall
 * ((stepCount + 1) * 100svh) and the stage sticks to the top of the viewport while
 * scroll progress through that extra height selects the active step.
 */
export function useHowItWorksScroll(stepCount: number): UseHowItWorksScrollResult {
  const sectionRef = useRef<HTMLElement>(null)
  const stageRef = useRef<HTMLDivElement>(null)
  const phoneColumnRef = useRef<HTMLDivElement>(null)
  const [activeStep, setActiveStep] = useState(0)
  const frame = useRef(0)
  const reducedMotion = useReducedMotion()

  useEffect(() => {
    const update = () => {
      frame.current = 0
      const section = sectionRef.current
      const stage = stageRef.current
      if (!section || !stage) return
      const rect = section.getBoundingClientRect()
      const travel = Math.max(1, section.offsetHeight - stage.offsetHeight)
      const progress = Math.max(0, Math.min(0.999, -rect.top / travel))
      const step = Math.min(stepCount - 1, Math.max(0, Math.floor(progress * stepCount)))
      setActiveStep(previous => (previous === step ? previous : step))
    }

    const queueUpdate = () => {
      if (!frame.current) frame.current = requestAnimationFrame(update)
    }

    queueUpdate()
    window.addEventListener('scroll', queueUpdate, { passive: true })
    window.addEventListener('resize', queueUpdate)
    return () => {
      window.removeEventListener('scroll', queueUpdate)
      window.removeEventListener('resize', queueUpdate)
      // Reset (not just cancel) so a StrictMode dev double-invoke doesn't leave a stale,
      // truthy frame id behind that would make the next queueUpdate() skip scheduling.
      if (frame.current) {
        cancelAnimationFrame(frame.current)
        frame.current = 0
      }
    }
  }, [stepCount])

  // Enables the .v2-hiw-snap stops. `proximity` only snaps near a stop, so the rest of the page scrolls freely.
  useEffect(() => {
    const root = document.documentElement
    const previous = root.style.scrollSnapType
    root.style.scrollSnapType = 'y proximity'
    return () => {
      root.style.scrollSnapType = previous
    }
  }, [])

  useEffect(() => {
    const phoneColumn = phoneColumnRef.current
    if (!phoneColumn) return

    const measure = () => {
      const style = getComputedStyle(phoneColumn)
      const height = phoneColumn.clientHeight - parseFloat(style.paddingTop || '0') - parseFloat(style.paddingBottom || '0')
      const width = phoneColumn.clientWidth - 32
      const zoom = Math.max(0.2, Math.min(4.5, height / PHONE_HEIGHT, width / PHONE_WIDTH))
      phoneColumn.style.setProperty('--phone-zoom', String(zoom))
    }

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(phoneColumn)
    document.fonts?.ready.then(measure).catch(() => undefined)
    return () => observer.disconnect()
  }, [])

  // Scroll to the middle of the step's slice of the section, so it is not right on a boundary.
  const scrollToStep = useCallback(
    (step: number) => {
      const section = sectionRef.current
      const stage = stageRef.current
      if (!section || !stage) return
      const travel = Math.max(1, section.offsetHeight - stage.offsetHeight)
      const sectionTop = section.getBoundingClientRect().top + window.scrollY
      window.scrollTo({ top: sectionTop + (travel * (step + 0.5)) / stepCount, behavior: reducedMotion ? 'auto' : 'smooth' })
    },
    [stepCount, reducedMotion],
  )

  return { sectionRef, stageRef, phoneColumnRef, activeStep, scrollToStep }
}
